import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { listTenants, AdminApiError } from '../../../shared/services/adminApi';

function StatusBadge({ status }) {
  const styles = {
    active: 'bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300',
    trial: 'bg-yellow-100 dark:bg-yellow-900/40 text-yellow-700 dark:text-yellow-300',
    suspended: 'bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300',
  };

  return (
    <span className={`px-2 py-1 text-xs font-medium rounded ${styles[status] || 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-200'}`}>
      {status.charAt(0).toUpperCase() + status.slice(1)}
    </span>
  );
}

function TierBadge({ tier }) {
  const styles = {
    pro: 'bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300',
    free: 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-200',
  };

  return (
    <span className={`px-2 py-1 text-xs font-medium rounded ${styles[tier] || 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-200'}`}>
      {tier.toUpperCase()}
    </span>
  );
}

export function TenantList() {
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    async function fetchTenants() {
      try {
        setLoading(true);
        setError(null);
        const response = await listTenants();
        // Flatten stats into tenant object for easier access
        const tenantsData = (response.data?.tenants || []).map(tenant => ({
          ...tenant,
          jobCount: tenant.stats?.jobCount || 0,
          quoteCount: tenant.stats?.quoteCount || 0,
          userCount: tenant.stats?.userCount || 0,
        }));
        setTenants(tenantsData);
      } catch (err) {
        console.error('Failed to load tenants:', err);
        if (err instanceof AdminApiError) {
          if (err.status === 401) {
            setError('Please log in to view tenants');
          } else if (err.status === 403) {
            setError('You do not have permission to view tenants');
          } else {
            setError(err.message || 'Failed to load tenants');
          }
        } else {
          setError('Failed to load tenants. Please check your connection.');
        }
      } finally {
        setLoading(false);
      }
    }

    fetchTenants();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg p-4 text-red-700 dark:text-red-300">
        {error}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Tenants</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Manage client accounts and their data
          </p>
        </div>
        <Link
          to="/admin/tenants/new"
          className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 transition-colors"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
          </svg>
          Add Tenant
        </Link>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-800 p-4">
          <p className="text-sm text-gray-500 dark:text-gray-400">Total Tenants</p>
          <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{tenants.length}</p>
        </div>
        <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-800 p-4">
          <p className="text-sm text-gray-500 dark:text-gray-400">Active</p>
          <p className="text-2xl font-bold text-green-600 dark:text-green-400">
            {tenants.filter(t => t.status === 'active').length}
          </p>
        </div>
        <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-800 p-4">
          <p className="text-sm text-gray-500 dark:text-gray-400">Pro Tier</p>
          <p className="text-2xl font-bold text-purple-600 dark:text-purple-400">
            {tenants.filter(t => t.tier === 'pro').length}
          </p>
        </div>
      </div>

      {/* Tenant Table */}
      <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-800 overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-800">
          <thead className="bg-gray-50 dark:bg-gray-900">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Company
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Subdomain
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Tier
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Jobs
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Quotes
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-800">
            {tenants.map((tenant) => (
              <tr key={tenant.tenantId} className="hover:bg-gray-50 dark:hover:bg-gray-800">
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                    {tenant.companyName}
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">
                    {tenant.industry}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <code className="text-sm text-gray-600 dark:text-gray-300 bg-gray-100 dark:bg-gray-800 px-2 py-1 rounded">
                    {tenant.subdomain}
                  </code>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <StatusBadge status={tenant.status} />
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <TierBadge tier={tenant.tier} />
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                  {tenant.jobCount}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                  {tenant.quoteCount}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                  <div className="flex items-center justify-end gap-2">
                    <Link
                      to={`/admin/tenants/${tenant.tenantId}`}
                      className="text-indigo-600 dark:text-indigo-400 hover:text-indigo-900"
                    >
                      View
                    </Link>
                    <Link
                      to={`/admin/tenants/${tenant.tenantId}/upload`}
                      className="text-green-600 dark:text-green-400 hover:text-green-900"
                    >
                      Upload CSV
                    </Link>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {tenants.length === 0 && (
          <div className="text-center py-12">
            <svg className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
            </svg>
            <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-gray-100">No tenants</h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">Get started by creating a new tenant.</p>
            <div className="mt-6">
              <Link
                to="/admin/tenants/new"
                className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
                Add Tenant
              </Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default TenantList;
